/**
 * Mock Users and Development Scenarios
 * Used for local development without requiring real authentication
 */

export interface MockUser {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'admin' | 'creator' | 'user';
  status: 'active' | 'inactive';
  organizationId?: string;
  createdAt?: string;
  lastLoginAt?: string;
}

export interface DevScenario {
  id: string;
  name: string;
  description: string;
  user: MockUser;
}

/**
 * Pre-defined mock users for testing different roles
 */
export const MOCK_USERS: Record<string, MockUser> = {
  admin: {
    userId: 'mock-admin-001',
    email: 'admin@pedalmart.dev',
    firstName: 'Admin',
    lastName: 'User',
    role: 'admin',
    status: 'active',
    organizationId: 'org-001',
  },
  creator: {
    userId: 'mock-creator-001',
    email: 'creator@pedalmart.dev',
    firstName: 'Creator',
    lastName: 'User',
    role: 'creator',
    status: 'active',
    organizationId: 'org-002',
  },
  user: {
    userId: 'mock-user-001',
    email: 'user@pedalmart.dev',
    firstName: 'Regular',
    lastName: 'User',
    role: 'user',
    status: 'active',
  },
  inactive: {
    userId: 'mock-inactive-001',
    email: 'inactive@pedalmart.dev',
    firstName: 'Inactive',
    lastName: 'User',
    role: 'user',
    status: 'inactive',
  },
};

/**
 * Development scenarios for testing different application states
 */
export const DEV_SCENARIOS: DevScenario[] = [
  {
    id: 'new-user',
    name: 'New User',
    description: 'Fresh user with no purchases or licenses',
    user: MOCK_USERS.user,
  },
  {
    id: 'creator-with-products',
    name: 'Creator with Products',
    description: 'Creator who has published products',
    user: MOCK_USERS.creator,
  },
  {
    id: 'admin-full-access',
    name: 'Admin Full Access',
    description: 'Admin with full system access',
    user: MOCK_USERS.admin,
  },
  {
    id: 'suspended-user',
    name: 'Suspended User',
    description: 'User with suspended account',
    user: MOCK_USERS.inactive,
  },
];

const STORAGE_KEY = 'mockUser';
const SCENARIO_KEY = 'activeScenario';

/**
 * Get the currently logged-in mock user from session storage
 */
export function getCurrentMockUser(): MockUser | null {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as MockUser;
  } catch {
    return null;
  }
}

/**
 * Set the current mock user by key (admin, creator, user, etc.)
 */
export function setCurrentMockUser(userKey: string): MockUser {
  const user = MOCK_USERS[userKey];
  if (!user) {
    throw new Error(`Unknown user key: ${userKey}`);
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  return user;
}

/**
 * Clear the current mock user (logout)
 */
export function clearCurrentMockUser(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(SCENARIO_KEY);
}

/**
 * Apply a development scenario
 */
export function applyDevScenario(scenarioId: string): MockUser | null {
  const scenario = DEV_SCENARIOS.find((s) => s.id === scenarioId);
  if (!scenario) {
    console.warn(`Unknown scenario: ${scenarioId}`);
    return null;
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(scenario.user));
  sessionStorage.setItem(SCENARIO_KEY, scenarioId);
  return scenario.user;
}

/**
 * Get the currently active scenario ID
 */
export function getActiveScenario(): string | null {
  return sessionStorage.getItem(SCENARIO_KEY);
}
